import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Nav,
  Tab,
  Spinner,
  Alert,
  Form,
  Badge,
} from "react-bootstrap";
import {
  IconHeadset,
  IconTicket,
  IconMessage,
  IconSettings,
  IconUsers,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import SupportChat from "./SupportChat";
import TicketSystem from "./TicketSystem";
import AgentManagement from "./AgentManagement";
import { 
  getSupportConfig, 
  createSupportConfig, 
  updateSupportConfig 
} from "../../../services/SupportServices";
import { isClientPlan, isAdvancedPlan } from "../../../utils/PlanUtils";

const Support = () => {
  const { user, getUserPlan } = useContext(UserContext);
  
  // Verificar permissões baseadas no plano
  const currentPlan = getUserPlan();
  const canConfigureSupport = user && isAdvancedPlan(currentPlan);
  const canRequestSupport = user && isClientPlan(currentPlan);
  
  const [activeTab, setActiveTab] = useState(() => {
    // Definir aba inicial baseada nas permissões e configuração
    if (canRequestSupport) {
      // Para planos 3 e 4, sempre começar com tickets se disponível
      if ([3, 4].includes(currentPlan)) {
        return "tickets";
      }
      // Para outros clientes, começar com chat
      return "chat";
    }
    if (canConfigureSupport) return "config";
    return "config";
  });
  const [supportConfig, setSupportConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  
  // Estados do formulário de configuração
  const [configForm, setConfigForm] = useState({
    support_type: [3, 4].includes(currentPlan) ? "ticket" : "both",
    is_active: true,
    business_hours_start: "08:00",
    business_hours_end: "18:00",
    timezone: "America/Sao_Paulo",
    auto_assign: false,
    welcome_message: ""
  });

  useEffect(() => {
    loadSupportConfig();
  }, []);

  const loadSupportConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await getSupportConfig();
      
      if (result.success) {
        if (result.data) {
          setSupportConfig(result.data);
          
          // Para planos 3 e 4, forçar support_type para "ticket"
          const adjustedConfig = { ...result.data };
          if ([3, 4].includes(currentPlan) && (result.data.support_type === "chat" || result.data.support_type === "both")) {
            adjustedConfig.support_type = "ticket";
          }
          
          setConfigForm(adjustedConfig);
          
          // Definir aba inicial baseada na configuração e permissões
          if (canRequestSupport) {
            // Para planos 3 e 4, sempre usar tickets quando disponível
            if ([3, 4].includes(currentPlan)) {
              if (result.data.support_type === "ticket" || result.data.support_type === "both") {
                setActiveTab("tickets");
              }
            } else {
              // Para outros clientes, definir baseado no tipo de suporte disponível
              if (result.data.support_type === "ticket") {
                setActiveTab("tickets");
              } else {
                setActiveTab("chat"); // chat ou both
              }
            }
          } else if (result.data.support_type === "ticket") {
            setActiveTab("tickets");
          } else if (result.data.support_type === "chat") {
            setActiveTab("chat");
          } else if (result.data.support_type === "both") {
            setActiveTab("chat");
          }
        } else {
          // Configuração não existe ainda
          setSupportConfig(null);
        }
      } else if (result.isApiUnavailable) {
        // API não está disponível, não mostrar erro
        setSupportConfig(null);
      } else {
        setError(result.error || "Erro ao carregar configuração do suporte");
      }
    } catch (err) {
      console.warn("Erro inesperado:", err.message);
      setSupportConfig(null);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsConfigLoading(true);
      setError(null);

      let result;
      if (supportConfig) {
        // Atualizar configuração existente
        result = await updateSupportConfig(supportConfig.id, configForm);
      } else {
        // Criar nova configuração
        result = await createSupportConfig(configForm);
      }

      if (result.success) {
        setSupportConfig(result.data);
        if (canRequestSupport) {
          // Para planos 3 e 4, sempre usar tickets quando disponível
          if ([3, 4].includes(currentPlan)) {
            if (result.data.support_type === "ticket" || result.data.support_type === "both") {
              setActiveTab("tickets");
            }
          } else {
            // Para outros clientes, definir baseado no tipo de suporte disponível
            if (result.data.support_type === "ticket") {
              setActiveTab("tickets");
            } else {
              setActiveTab("chat"); // chat ou both
            }
          }
        } else {
          setActiveTab(result.data.support_type === "ticket" ? "tickets" : "chat");
        }
      } else if (result.isApiUnavailable) {
        setError("API de suporte ainda não está disponível no backend");
      } else {
        setError(result.error || "Erro ao salvar configuração do suporte");
      }
    } catch (err) {
      console.warn("Erro inesperado ao salvar configuração:", err.message);
      setError("Erro inesperado ao salvar configuração");
    } finally {
      setIsConfigLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Verificar se o usuário tem permissão para acessar o sistema de suporte
  if (!canConfigureSupport && !canRequestSupport) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          <h5>Acesso Negado</h5>
          <p className="mb-0">
            Você não possui permissões para acessar o sistema de suporte. 
            Entre em contato com o administrador da sua organização.
          </p>
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Carregando configurações...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="">
      <Row>
        <Col>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-3">
              <IconHeadset size={32} className="text-primary" />
              <div>
                <h2 className="mb-1">Central de Suporte</h2>
                <p className="text-muted mb-0">
                  Gerencie tickets e chat de suporte da sua organização
                </p>
              </div>
            </div>

            {supportConfig && (
              <Badge
                bg={supportConfig.is_active ? "success" : "secondary"}
                className="fs-6 px-3 py-2"
              >
                {supportConfig.is_active ? "Ativo" : "Inativo"}
              </Badge>
            )}
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

         

          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Nav variant="pills" className="mb-4 bg-light p-2 rounded">
              {/* Abas para clientes e administradores */}
              {supportConfig && supportConfig.is_active && (
                <>
                  {/* Aba de Chat - disponível para todos quando configurado, exceto planos 3 e 4 */}
                  {(supportConfig.support_type === "chat" || supportConfig.support_type === "both") && ![3, 4].includes(currentPlan) && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="chat"
                        className="d-flex align-items-center gap-2"
                      >
                        <IconMessage size={18} />
                        {canRequestSupport ? "Chat" : "Chat ao Vivo"}
                      </Nav.Link>
                    </Nav.Item>
                  )}

                  {/* Aba de Tickets - disponível para todos quando configurado */}
                  {(supportConfig.support_type === "ticket" || supportConfig.support_type === "both") && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="tickets"
                        className="d-flex align-items-center gap-2"
                      >
                        <IconTicket size={18} />
                        {canRequestSupport ? "Tickets" : "Tickets"}
                      </Nav.Link>
                    </Nav.Item>
                  )}

                  {/* Aba de Agentes - apenas para administradores */}
                  {canConfigureSupport && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="agents"
                        className="d-flex align-items-center gap-2"
                      >
                        <IconUsers size={18} />
                        Agentes
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </>
              )}
              
              {/* Aba de Configuração - apenas para administradores */}
              {canConfigureSupport && (
                <Nav.Item>
                  <Nav.Link
                    eventKey="config"
                    className="d-flex align-items-center gap-2"
                  >
                    <IconSettings size={18} />
                    Configuração
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>

            <Tab.Content>
              {/* Aba de Chat - oculta para planos 3 e 4 */}
              {![3, 4].includes(currentPlan) && (
                <Tab.Pane eventKey="chat">
                  <SupportChat 
                    config={supportConfig} 
                    isClientView={canRequestSupport}
                  />
                </Tab.Pane>
              )}

              {/* Aba de Tickets */}
              <Tab.Pane eventKey="tickets">
                <TicketSystem 
                  config={supportConfig} 
                  isClientView={canRequestSupport}
                />
              </Tab.Pane>

              {/* Abas administrativas - apenas para planos avançados */}
              {canConfigureSupport && (
                <>
                  <Tab.Pane eventKey="config">
                    <Card>
                      <Card.Header>
                        <h5 className="mb-0">
                          <IconSettings className="me-2" />
                          Configuração do Sistema de Suporte
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Form onSubmit={handleConfigSubmit}>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Tipo de Suporte</Form.Label>
                                <Form.Select
                                  value={configForm.support_type}
                                  onChange={(e) =>
                                    handleConfigChange(
                                      "support_type",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="ticket">Apenas Tickets</option>
                                  {/* Opções de Chat apenas para planos diferentes de 3 e 4 */}
                                  {![3, 4].includes(currentPlan) && (
                                    <>
                                      <option value="chat">Apenas Chat</option>
                                      <option value="both">Tickets e Chat</option>
                                    </>
                                  )}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  {[3, 4].includes(currentPlan) 
                                    ? "Seu plano permite apenas suporte via tickets"
                                    : "Escolha o tipo de atendimento que sua organização oferecerá"
                                  }
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Check
                                  type="switch"
                                  id="is_active"
                                  label={
                                    configForm.is_active
                                      ? "Sistema Ativo"
                                      : "Sistema Inativo"
                                  }
                                  checked={configForm.is_active}
                                  onChange={(e) =>
                                    handleConfigChange(
                                      "is_active",
                                      e.target.checked
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Horário de Início</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={configForm.business_hours_start}
                                  onChange={(e) =>
                                    handleConfigChange(
                                      "business_hours_start",
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Horário de Fim</Form.Label>
                                <Form.Control
                                  type="time"
                                  value={configForm.business_hours_end}
                                  onChange={(e) =>
                                    handleConfigChange(
                                      "business_hours_end",
                                      e.target.value
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Fuso Horário</Form.Label>
                                <Form.Select
                                  value={configForm.timezone}
                                  onChange={(e) =>
                                    handleConfigChange("timezone", e.target.value)
                                  }
                                >
                                  <option value="America/Sao_Paulo">
                                    América/São Paulo
                                  </option>
                                  <option value="America/Rio_Branco">
                                    América/Rio Branco
                                  </option>
                                  <option value="America/Manaus">
                                    América/Manaus
                                  </option>
                                  <option value="America/Fortaleza">
                                    América/Fortaleza
                                  </option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Form.Group className="mb-3">
                            <Form.Label>Atribuição Automática</Form.Label>
                            <Form.Check
                              type="switch"
                              id="auto_assign"
                              label={
                                configForm.auto_assign
                                  ? "Atribuir automaticamente aos agentes"
                                  : "Atribuição manual"
                              }
                              checked={configForm.auto_assign}
                              onChange={(e) =>
                                handleConfigChange(
                                  "auto_assign",
                                  e.target.checked
                                )
                              }
                            />
                            <Form.Text className="text-muted">
                              Quando ativo, tickets e chats serão automaticamente
                              atribuídos aos agentes disponíveis
                            </Form.Text>
                          </Form.Group>

                          <Form.Group className="mb-4">
                            <Form.Label>Mensagem de Boas-vindas</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={configForm.welcome_message}
                              onChange={(e) =>
                                handleConfigChange(
                                  "welcome_message",
                                  e.target.value
                                )
                              }
                              placeholder="Digite a mensagem de boas-vindas que será exibida aos clientes"
                            />
                          </Form.Group>

                          <div className="d-flex gap-2">
                            <Button
                              type="submit"
                              variant="primary"
                              disabled={isConfigLoading}
                            >
                              {isConfigLoading && (
                                <Spinner size="sm" className="me-2" />
                              )}
                              {supportConfig
                                ? "Atualizar Configuração"
                                : "Criar Configuração"}
                            </Button>

                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={loadSupportConfig}
                              disabled={isConfigLoading}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Tab.Pane>

                  <Tab.Pane eventKey="agents">
                    <AgentManagement config={supportConfig} />
                  </Tab.Pane>
                </>
              )}
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>
    </Container>
  );
};

export default Support;